import { Div, Flex, Text, Tooltip } from '@sendoutcards/quantum-design-ui'
import React from 'react'
import { TotalDiscounts } from 'src/dashboard/components/TotalDiscounts/TotalDiscounts'
import { formatCost } from 'src/helpers'
import {
  useAccount,
  useActions,
  useFlagsWithUseQueries,
  useSelector,
  useState,
  useSubscriptions,
} from 'src/hooks'
import { OrderApiType } from 'src/orders/api'
import {
  getGrandTotal,
  getShippingInfo,
  getSubTotalText,
  getTotalGiftFeeAmount,
} from 'src/orders/helpers'
import useGetDiscountBreakDown from 'src/orders/hooks/useGetDiscountBreakdown'
import { OrderWithPartialLines } from 'src/redux/reducers/orders'
import { useUpdateOrder } from 'src/react_query'
import MembershipRewardsTool from '../MembershipRewardsTool/MembershipRewardsTool'
export interface OrderTotalProps {
  order?: OrderWithPartialLines
  api?: OrderApiType
}

const styles = {
  item: {
    '@media (max-width: 840px)': {
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  grandTotal: {
    '@media (max-width: 840px)': {
      flexDirection: 'column-reverse' as const,
    },
  },
  cancelOrder: {
    '@media (max-width: 840px)': {
      marginLeft: '0',
    },
  },
}

const OrderTotal: React.FC<OrderTotalProps> = props => {
  const { order, api } = props

  const toggle = (prevState: boolean): boolean => !prevState
  const totalsContainerWidth = '534px'

  // Hooks
  const account = useAccount()
  const actions = useActions()
  const subscription = useSubscriptions()
  const { hasMembershipRewards } = useFlagsWithUseQueries()
  const { isMobile } = useSelector(state => state.window)
  const updateOrderMutation = useUpdateOrder()

  const [currentMR, setCurrentMR] = useState(order?.points.toAllocate ?? 0)

  const shippingInfo = getShippingInfo(account.giftShippingDiscount, order)

  const totalGiftFeeAmount = getTotalGiftFeeAmount(order)

  const grandTotal = getGrandTotal(order)

  const isMissingRecipient = order && !order.cost.entries[0]

  const { discountItems } = useGetDiscountBreakDown(order)

  const totals = [
    {
      id: 'subtotal',
      title: 'Subtotal:',
      cost: getSubTotalText(order),
      shouldDisplay: true,
    },
    {
      id: 'giftProcessingFee',
      title: 'Gift Processing Fees:',
      cost: formatCost(totalGiftFeeAmount),
      shouldDisplay: totalGiftFeeAmount > 0,
      shouldDisplayTooltip: isMissingRecipient,
      tooltipMessage: 'Add Recipient(s) to calculate shipping.',
    },
    {
      id: 'shipping',
      title: 'Shipping:',
      cost: shippingInfo.totalShippingText,
      shouldDisplay: true,
    },
  ]

  const membershipRewardsAvailable = account.points.amount

  const maxUsableMembershipRewards = order?.points.maxToAllocate ?? 0

  const updateMR = async (newMRAmount: number) => {
    const max = maxUsableMembershipRewards
    const min = 50
    const clampedMRAmount =
      newMRAmount !== 0 ? Math.min(max, Math.max(min, newMRAmount)) : 0
    setCurrentMR(clampedMRAmount)
    if (order) {
      const { updateOrder } = await updateOrderMutation.mutateAsync({
        order: { id: order.id, pointsToAllocate: clampedMRAmount },
      })
      actions.savedOrder(updateOrder.order)
      return updateOrder.order.points.toAllocate === 0 ? 'initial' : 'applied'
    }
    return newMRAmount === 0 ? 'initial' : 'applied'
  }

  return (
    <Div
      display="flex"
      width="100%"
      flexDirection="column"
      backgroundColor="background"
      borderRadius="0 0 28px 28px"
      alignItems="flex-end"
      css={styles.item}
    >
      {totals.map((total, index) => {
        return (
          <>
            {total.shouldDisplay && (
              <Flex
                width="100%"
                maxWidth={totalsContainerWidth}
                key={index}
                justifyContent="space-between"
                alignItems="flex-end"
                flexDirection="row"
                inset={{
                  vertical: 'x2',
                  right: !isMobile ? 'x6' : 'x3',
                  left: !isMobile ? undefined : 'x3',
                }}
              >
                <Text
                  color="primaryHeading"
                  weight="semiBold"
                  type="largeBody"
                  content={total.title}
                  whiteSpace="nowrap"
                />
                <Div>
                  <Text
                    color="primaryHeading"
                    weight="bold"
                    type="subtitle"
                    content={total.cost}
                  />
                </Div>

                {total.shouldDisplayTooltip && (
                  <Flex position="absolute" bottom="-36px">
                    <Tooltip title={total.tooltipMessage} direction="top" />
                  </Flex>
                )}
              </Flex>
            )}
          </>
        )
      })}
      {discountItems.length > 0 && (
        <Div
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="flex-end"
          flexDirection={isMobile ? 'column' : 'row'}
          css={styles.item}
        >
          <Div
            width="100%"
            justifyContent="flex-end"
            css={styles.item}
            display="flex"
          >
            <Div
              width="100%"
              maxWidth={`calc(${totalsContainerWidth} - 20px)`}
              outset={{
                right: !isMobile ? 'x4' : 'x2',
                bottom: 'x2',
                left: isMobile ? 'x2' : undefined,
              }}
            >
              <TotalDiscounts
                title="Discounts & Savings Breakdown"
                planDescription={subscription.currentPlan.title}
                discountData={discountItems}
              />
            </Div>
          </Div>
        </Div>
      )}
      {hasMembershipRewards ? (
        <MembershipRewardsTool
          containerMaxWidth={`calc(${totalsContainerWidth} - 20px)`}
          currentMR={currentMR}
          maxUsableMRs={maxUsableMembershipRewards}
          onMRUpdate={updateMR}
          totalMRAvailable={membershipRewardsAvailable}
          onAddRecipients={
            order?.contacts.length === 0 ? api?.handleOpenDrawer : undefined
          }
        />
      ) : null}
      <Flex
        width="100%"
        justifyContent="space-between"
        alignItems="flex-end"
        flexDirection={isMobile ? 'column' : 'row'}
      >
        <Div
          display="flex"
          alignItems="center"
          inset={{ vertical: '32px' }}
          backgroundColor="#EBEBEB"
          borderRadius={{ bottom: '16px' }}
          width="100%"
          justifyContent="space-between"
          flexWrap="wrap-reverse"
          css={styles.grandTotal}
        >
          <Flex
            height="auto"
            justifyContent="space-between"
            position="relative"
            cursor="pointer"
            onClick={() => api?.setIsDeleteModalOpen(toggle)}
          >
            <Div outset={{ left: 'x4' }} css={styles.cancelOrder}>
              <Text
                type={isMobile ? 'caption' : 'body'}
                whiteSpace="nowrap"
                weight="bold"
                content="Cancel Order"
                color="danger"
              />
            </Div>
          </Flex>
          <Flex
            width="100%"
            maxWidth={totalsContainerWidth}
            key="final"
            justifyContent="space-between"
            alignItems="center"
            inset={{
              right: !isMobile ? 'x6' : 'x3',
              left: !isMobile ? undefined : 'x3',
            }}
          >
            <Text
              color="primaryHeading"
              weight="semiBold"
              type="largeBody"
              content="Total:"
              whiteSpace="nowrap"
              alignment="right"
            />
            <Div>
              <Text
                color="primaryHeading"
                weight="bold"
                type="subtitle"
                content={grandTotal}
              />
            </Div>
          </Flex>
        </Div>
      </Flex>
    </Div>
  )
}

export default OrderTotal
